import React, { useState } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { PRODUCT_ANNOUNCEMENT_KINDS } from "utils/productAnnouncement";

const appAudienceLabels = {
  mobileUser: "Agents",
  mobileVendor: "Vendors",
  mobileBuyer: "Buyers",
};

const MediaCarousel = ({ media }) => {
  const [index, setIndex] = useState(0);
  if (!media?.length) return null;
  const current = media[Math.min(index, media.length - 1)];

  return (
    <div>
      <div
        className="flex snap-x snap-mandatory overflow-x-auto rounded-2xl bg-stone-100 [scrollbar-width:none]"
        onScroll={(event) =>
          setIndex(
            Math.round(
              event.currentTarget.scrollLeft / event.currentTarget.clientWidth,
            ),
          )
        }
      >
        {media.map((asset) => (
          <div
            key={asset._id || asset.clientId || asset.url}
            className="aspect-[16/10] w-full shrink-0 snap-center"
          >
            {asset.type === "video" ? (
              <video
                controls
                src={asset.url}
                poster={asset.posterUrl}
                className="h-full w-full bg-black object-contain"
              />
            ) : (
              <img
                src={asset.url}
                alt={asset.altText || "Announcement preview"}
                className="h-full w-full object-cover"
              />
            )}
          </div>
        ))}
      </div>
      {current.caption && (
        <p className="mt-2 text-[13px] leading-[18px] text-stone-500">
          {current.caption}
        </p>
      )}
      {media.length > 1 && (
        <div className="mt-2.5 flex justify-center gap-1.5">
          {media.map((asset, assetIndex) => (
            <span
              key={asset._id || asset.clientId || asset.url}
              className={`h-1.5 rounded-full ${
                assetIndex === index ? "w-[18px] bg-black" : "w-1.5 bg-stone-300"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AnnouncementPreview = ({ announcement }) => {
  const kindTag =
    PRODUCT_ANNOUNCEMENT_KINDS.find((kind) => kind.value === announcement.kind)
      ?.tag || "New feature";
  const actions = (announcement.actions || []).filter(
    (action) => action.label && (action.appPath || action.externalUrl),
  );
  const hasPrimaryAction = actions.some((action) => action.style !== "secondary");
  const audience = (announcement.audience?.targets || [])
    .map((target) => appAudienceLabels[target])
    .filter(Boolean)
    .join(", ");
  const highlights = (announcement.highlights || []).filter((item) =>
    item.trim(),
  );

  return (
    <div>
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-darkgray">
        Mobile app preview{audience ? ` · ${audience}` : " · No audience yet"}
      </div>
      <div className="mx-auto w-full max-w-[360px] overflow-hidden rounded-[36px] border-[10px] border-stone-900 bg-stone-900">
        <div className="relative flex h-[680px] flex-col justify-end bg-stone-200">
          <div className="absolute inset-0 bg-black/45" />
          <div className="relative flex max-h-[92%] flex-col rounded-t-3xl bg-white">
            <div className="flex justify-center pb-1 pt-3">
              <span className="h-1 w-10 rounded-full bg-stone-200" />
            </div>
            <div className="relative flex h-14 items-center border-b border-stone-200 px-4">
              <span className="text-base font-semibold text-black">
                What&apos;s new in Beleef
              </span>
              <span className="absolute right-2.5 top-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-stone-100 text-stone-500">
                <FiX size={18} />
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-5">
              <MediaCarousel media={announcement.media} />
              <p
                className={`${
                  announcement.media?.length ? "mt-5" : "mt-1"
                } text-xs font-bold uppercase tracking-[0.07em] text-purple-800`}
              >
                {kindTag}
              </p>
              <p className="mt-1.5 text-2xl font-bold leading-[30px] text-black">
                {announcement.title || "Your announcement headline"}
              </p>
              <p className="mt-2.5 whitespace-pre-wrap text-[15px] leading-[23px] text-[#818791]">
                {announcement.body ||
                  "The announcement message will appear here."}
              </p>
              {highlights.length > 0 && (
                <ul className="mt-[18px] space-y-3">
                  {highlights.map((highlight, index) => (
                    <li key={`${highlight}-${index}`} className="flex gap-2.5">
                      <span className="mt-px flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700">
                        <FiCheck size={14} />
                      </span>
                      <span className="text-[15px] leading-[22px] text-gray-900">
                        {highlight}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="space-y-2.5 border-t border-stone-200 px-4 pb-6 pt-3">
              {actions.map((action, index) => (
                <div
                  key={action._id || action.clientId || `${action.label}-${index}`}
                  className={`rounded-xl border py-3.5 text-center text-[15px] font-semibold ${
                    action.style === "secondary"
                      ? "border-stone-300 bg-white text-black"
                      : "border-black bg-black text-white"
                  }`}
                >
                  {action.label}
                </div>
              ))}
              <div
                className={`rounded-xl border py-3.5 text-center text-[15px] font-semibold ${
                  hasPrimaryAction
                    ? "border-stone-300 bg-white text-black"
                    : "border-black bg-black text-white"
                }`}
              >
                Got it
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPreview;
